import { ReactComponent as CloseIcon } from "../../img/iconImgAllClose.svg";
import { useEffect, useState } from "react";
import PlaceHolderTextField from "../common/PlaceHolderTextField";
import styles from "./GroupAddScreen.module.css";
import { useTranslation } from "react-i18next";
import Language from "../language/Language";
import BigButton from "../common/BigButton";
import VoiceLanguageSelectScreen from "../voice/view/VoiceLanguageSelectScreen";
import {
  addGroup,
  mergeGroups,
  updateGroup as updateGroupFromAPI,
} from "./groupAPI";
import { Status } from "../common/Status";
import LoadingScreen from "../common/LoadingScreen";
import Group, { isValidateGroupName } from "./Group";
import { getLanguageFromCode } from "../voice/util/languages";
import {
  getGroupLatestLanguageCode,
  setGroupLatestLanguageCode,
} from "./utils/groupSettings";
import ContainerNavigationView from "../common/view/ContainerNavigationView";
import ListSettingView from "../common/ListSettingView";
import OnevocaDialog from "../common/OnevocaDialog";
import { useBackToClose } from "../../hooks/useBackToClose";

interface GroupAddScreenProps {
  open: boolean;
  collectionId: number;
  group: Group | null;
  selectedGroups: Group[] | null;
  onCreateGroup: (groupname: string) => void;
  onUpdateGroup: () => void;
  onClose: () => void;
}

export default function GroupAddScreen(props: GroupAddScreenProps) {
  useBackToClose(props.open, props.onClose);
  const { t } = useTranslation();
  const [selectedGroupLanguage, setSelectedGroupLanguage] =
    useState<Language>();
  const [currentLanguageName, setCurrentLanguageName] = useState("");
  const [currentGroupName, setCurrentGroupName] = useState("");
  const [isAllowDone, setIsAllowDone] = useState(false);

  const [openLanguageSelectScreen, setOpenLanguageSelectScreen] =
    useState(false);
  const [openGroupAddDialog, setOpenGroupAddDialog] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [groupAddStatus, setGroupAddStatus] = useState(Status.Ready);
  const [groupUpdateStatus, setGroupUpdateStatus] = useState(Status.Ready);

  useEffect(() => {
    if (props.open === true) {
      if (props.group === null) {
        setCurrentGroupName("");

        const lagnuageCode = getGroupLatestLanguageCode();
        const language =
          lagnuageCode === null ? undefined : getLanguageFromCode(lagnuageCode);
        setSelectedGroupLanguage(language);

        updateCurrentLanguageName(language ?? undefined);
      } else {
        setCurrentGroupName(props.group.title);
        setSelectedGroupLanguage(
          getLanguageFromCode(props.group.language?.code ?? "")
        );
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    setIsAllowDone(checkIsAllowDone());
    updateCurrentLanguageName(selectedGroupLanguage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupLanguage]);

  useEffect(() => {
    setIsAllowDone(checkIsAllowDone());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupName]);

  useEffect(() => {
    switch (groupAddStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        props.onCreateGroup(currentGroupName);
        props.onClose();
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupAddStatus]);

  useEffect(() => {
    switch (groupUpdateStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        props.onUpdateGroup();
        props.onClose();
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupUpdateStatus]);

  function getGroupAddMessage(): string {
    if (selectedGroupLanguage === undefined) return "";
    const languageName = selectedGroupLanguage.name!;
    const message = t("CheckGroupLanguageMessage", { languageName });
    return message;
  }

  function checkIsAllowDone(): boolean {
    if (selectedGroupLanguage === undefined) {
      return false;
    } else if (currentGroupName.length === 0 || currentGroupName.length > 50) {
      return false;
    } else {
      return true;
    }
  }

  function updateCurrentLanguageName(selectedLanguage: Language | undefined) {
    const isValidLanguage =
      selectedLanguage !== undefined && selectedLanguage !== null;
    if (isValidLanguage) {
      setCurrentLanguageName(selectedLanguage.name);
    } else {
      setCurrentLanguageName(t("SelectLanguage"));
    }
  }

  function resetData() {
    setCurrentGroupName("");
    setSelectedGroupLanguage(undefined);
    setCurrentLanguageName("");
  }

  function handleOnClickBackground() {
    resetData();
    props.onClose();
  }

  function handleOnClickContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  function handleOnClickLanguage(language: Language) {
    setGroupLatestLanguageCode(language.code);
    setSelectedGroupLanguage(language);
  }

  async function handleOnClickDoneButton() {
    if (!isValidateGroupName(currentGroupName)) return;
    if (selectedGroupLanguage === undefined) return;

    if (props.selectedGroups !== null) {
      mergeGroup();
    } else if (props.group === null) {
      showGroupAddAlertWithLanguageMessage();
    } else {
      updateGroup();
    }
  }

  function showGroupAddAlertWithLanguageMessage() {
    if (selectedGroupLanguage === undefined) return;
    setOpenGroupAddDialog(true);
  }

  async function createGroup() {
    setGroupAddStatus(Status.Loading);
    try {
      await addGroup(
        props.collectionId,
        currentGroupName,
        selectedGroupLanguage!.code
      );
      setGroupAddStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setGroupAddStatus(Status.Ready);
    }
  }

  async function updateGroup() {
    setGroupUpdateStatus(Status.Loading);
    try {
      await updateGroupFromAPI(
        props.group!.title,
        currentGroupName,
        selectedGroupLanguage!.code
      );
      setGroupUpdateStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setGroupUpdateStatus(Status.Ready);
    }
  }

  async function mergeGroup() {
    setGroupUpdateStatus(Status.Loading);
    const grouopnames = props.selectedGroups!.map((group) => group.title);
    try {
      await mergeGroups(
        grouopnames,
        currentGroupName,
        selectedGroupLanguage!.code
      );
      setGroupUpdateStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setGroupUpdateStatus(Status.Ready);
    }
  }

  if (props.open === true) {
    return (
      <>
        <div className={styles.stack}>
          <div
            className={styles.container}
            onClick={() => handleOnClickBackground()}
          >
            <div
              className={styles.groupAddContainer}
              onClick={handleOnClickContainer}
            >
              <ContainerNavigationView
                open={true}
                title={props.group === null ? t("GroupAddButton") : t("Edit")}
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={props.onClose}
              />
              <div className={styles.contentContainer}>
                <div className={styles.inputContainer}>
                  <PlaceHolderTextField
                    text={currentGroupName}
                    hint={t("GroupnameTitle")}
                    maxCount={50}
                    lineStyle="single"
                    handleTextChange={(text: string) => {
                      setCurrentGroupName(text);
                    }}
                  />
                </div>

                <div className={styles.languageSettingContainer}>
                  <ListSettingView
                    title={t("GROUP_LANGUAGE_SETTING_TITLE")}
                    description={t("GROUP_LANGUAGE_SETTING_DESCRIPTION")}
                    valueStyle="stringValue"
                    value={currentLanguageName}
                    handleOnClick={() => {
                      setOpenLanguageSelectScreen(true);
                    }}
                  />
                </div>

                <div className={styles.bottomButtonContainer}>
                  <BigButton
                    title={t("Done")}
                    variant={isAllowDone ? "normal" : "disable"}
                    handleClick={() => {
                      if (!isAllowDone) return;
                      handleOnClickDoneButton();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <VoiceLanguageSelectScreen
            open={openLanguageSelectScreen === true}
            handleOnClickLanguage={handleOnClickLanguage}
            handleOnClose={() => {
              setOpenLanguageSelectScreen(false);
            }}
          />
          <OnevocaDialog
            open={openGroupAddDialog}
            title={currentGroupName}
            message={getGroupAddMessage()}
            buttons={[
              {
                title: t("Confirmation"),
                style: "solid",
                onClick: () => {
                  setOpenGroupAddDialog(false);
                  createGroup();
                },
              },
              {
                title: t("Cancel"),
                style: "normal",
                onClick: () => {
                  setOpenGroupAddDialog(false);
                },
              },
            ]}
          />
          {isLoading === true ? <LoadingScreen /> : null}
        </div>
      </>
    );
  } else {
    return null;
  }
}
